<template>
    <section class="flex flex-col items-stretch mb-4 px-4 md:px-8 text-zinc-900 dark:text-zinc-100">
        <div class="flex flex-wrap justify-center gap-4 w-full">
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="from-date">Desde</label>
                <Calendar id="from-date" v-model="fromDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="to-date">Hasta</label>
                <Calendar id="to-date" v-model="toDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="company">Empresa</label>
                <Dropdown id="company" v-model="selectedCompany" :options="companies" optionLabel="name" placeholder="Selecciona una empresa" class="w-full"
                @change="companyChange" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="branch">Sucursal</label>
                <Dropdown id="branch" v-model="selectedBranch" :options="selectedCompany.branches" optionLabel="name" placeholder="Selecciona una sucursal"
                class="w-full" :disabled="formDisabled" filter/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="opacity-0" for="show-btn">Acción</label>
                <Button id="show-btn" label="Mostrar" @click="fillTable" :disabled="buttonDisabled"></Button>
            </div>
        </div>
    </section>
    <div class="mt-4 mb-4 px-4 md:px-8">
        <p><i class="pi pi-exclamation-triangle text-yellow-300"></i> El seleccionar una fecha muy amplia puede crear inestabilidad en el sistema, se recomienda precaución.</p>
    </div>
    <section class="grid justify-center px-4 md:px-8 text-zinc-900 dark:text-zinc-100" v-show="expenses.length > 0">
        <div class="flex flex-wrap justify-between items-center gap-4 mb-4">
            <div>
                <div>
                    <h2 class="text-left">Análisis de gastos</h2>
                </div>
                <div>
                    <h3 class="text-left">{{ subtitle }}</h3>
                </div>
            </div>
            <div class="bg-zinc-200 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 rounded-lg px-4 py-2 flex flex-wrap items-center shadow" v-show="expenses.length > 0">
                <span class="text-sm mb-2 font-semibold self-center px-2">Ordenar por:</span>
                <div class="flex items-center gap-2">
                    <Button icon="pi pi-money-bill" text rounded value="total" class="order-table-btn"
                    @click="sortTables('total')" v-tooltip.bottom="'Total'"/>
                    <Button icon="pi pi-sort-alpha-down" text rounded value="category" class="order-table-btn"
                    @click="sortTables('category')" v-tooltip.bottom="'Categoría'"/>
                </div>
            </div>
        </div>
        <div class="card overflow-auto text-zinc-900 dark:text-zinc-100">
            <DataTable
                :value="expenses"
                stripedRows
                showGridlines
                tableStyle="min-width: 30rem"
                class="min-w-full expenses-table"
                scrollable
                scrollHeight="525px">
                <Column v-for="col of columns" :key="col.field" :field="col.field" :header="col.header">
                    <template #body="{data, field}">
                        <span>
                            {{ data[field] }}
                        </span>
                    </template>
                </Column>
            </DataTable>
        </div>
    </section>
</template>

<script>
    import axiosInstance from '../axiosInstance';
    export default {
        data() {
            return {
                fromDate: new Date(),
                toDate: new Date(),
                selectedCompany: {},
                companies: [],
                selectedBranch: {},
                columns: [],
                expenses: [],
                subtitle: "",
                buttonDisabled: false,
                formDisabled: false,
                sortBy: null
            }
        },
        methods: {
            /**
             * Fetch initial data for expenses analysis (companies, default selections, date range)
             */
            async fetchExpensesInitialData() {
                this.$emit("update-loading", true);
                this.buttonDisabled = true;
                this.formDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/expenses_viewer", {
                        token: localStorage.getItem('token')
                    });

                    data = await response;
                } catch(error) {
                    console.error("fetchExpensesInitialData: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                }

                return data;
            },

            /**
             * Set initial data for the component (companies, branches, date range, default selections)
             */
            setInitialData() {
                this.fetchExpensesInitialData().then((response) => {
                    if(response.status === 200){
                        const { from: fromDate, to: toDate, defaultCmp, companies } = response.data;
                        this.fromDate = this.obtainDateType(fromDate);
                        this.toDate = this.obtainDateType(toDate);

                        this.companies = companies.map((company) => {
                            return {
                                name: company.data.name,
                                id: company.data.id,
                                branches: company.branches
                            }
                        });

                        this.selectedCompany = {
                            name: defaultCmp.data.name, 
                            id: defaultCmp.data.id, 
                            branches: defaultCmp.branches
                        }

                        this.selectedBranch = this.selectedCompany.branches[0];
                        this.buttonDisabled = false;
                        this.formDisabled = false;
                    } else {
                        console.warn("setInitialData: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            /**
             * Fetch expenses data based on selected filters (date range, company, branch)
             */
            async fetchExpensesData() {
                this.$emit("update-loading", true);
                this.buttonDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/expenses_viewer_data", {
                        token: localStorage.getItem('token'),
                        from: this.formatDate(this.fromDate),
                        to: this.formatDate(this.toDate),
                        company_id: this.selectedCompany.id,
                        branch_id: this.selectedBranch.id
                    });

                    data = await response;
                } catch(error) {
                    console.error("fetchExpensesData: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                    this.buttonDisabled = false;
                }

                return data;
            },

            /**
             * Fill the table with expenses data based on selected filters (date range, company, branch)
             */
            fillTable(){
                this.fetchExpensesData().then((response) => {
                    if(response.status == 200) {
                        const { departments, categories, subtitle } = response.data;
                        this.columns = [{ field: "category", header: "Categoría" }];
                        for(const dep of departments) {
                            this.columns.push({field: dep.toLowerCase(), header: dep})
                        }

                        this.expenses = Object.values(categories).map((elem) => {
                            const newEntry = { category: elem.name };
                            for(let i = 1; i < this.columns.length; i++) {
                                newEntry[this.columns[i].field] = elem.data[i-1] != "" ? elem.data[i-1] :"0.00" ;
                            }
                            return newEntry;
                        })

                        this.subtitle = subtitle;
                    } else {
                        console.warn("fillTable: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            sortTables(criterion){
                this.sortBy = criterion;
                if (this.expenses.length < 2) return;

                const expensesWithoutTotals = this.expenses.slice(0, -1);
                const totals = this.expenses[this.expenses.length - 1];

                let sorted = [...expensesWithoutTotals];
                if (criterion === 'total') {
                    sorted.sort((a, b) => {
                        const aNum = parseFloat((a.total || '0').toString().replace(/,/g, ''));
                        const bNum = parseFloat((b.total || '0').toString().replace(/,/g, ''));
                        return bNum - aNum;
                    });
                } else if (criterion === 'category') {
                    sorted.sort((a, b) => a.category.trim().localeCompare(b.category.trim()));
                }

                this.expenses = [...sorted, totals];
            },

            /**
             * Obtain a Date object from a date string
             * @param date - date string in format YYYY-MM-DD
             * @returns Date object
             */
            obtainDateType(date){
                const [dateY, dateM, dateD] = date.split("-");
                return new Date(Number(dateY), Number(dateM) - 1, Number(dateD))
            },

            /**
             * Format a Date object to a string in format YYYY-MM-DD
             * @param date - Date object
             * @returns formatted date string
             */
            formatDate(date) {
                const month = ((date.getMonth()+1).toString()).padStart(2, "0");
                const day = ((date.getDate()).toString()).padStart(2, "0");
                return `${date.getFullYear()}-${month}-${day}`
            },

            companyChange() {
                this.selectedBranch = this.selectedCompany.branches[0];
            }
        },
        mounted() {
            this.setInitialData();
        }
    }
</script>

<style scoped>
::v-deep(.p-datatable) {
    border-radius: 0.5rem !important;
    overflow: hidden !important;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
}

::v-deep(.p-datatable tbody tr td) {
    text-align: right !important;
}

::v-deep(.expenses-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
}

::v-deep(.expenses-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.expenses-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.expenses-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child) {
    text-align: left !important;
    /* background-color: #fecaca !important; */
    color: #ab0000 !important;
    font-weight: 600 !important;
}

::v-deep(.expenses-table.p-datatable .p-datatable-tbody > tr:last-child > td) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
}

::v-deep(.expenses-table.p-datatable .p-datatable-tbody > tr:last-child > td:first-child) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
    text-align: left !important;
}

#show-btn {
    @apply bg-zinc-200 text-zinc-900 border border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 rounded px-4 py-2 text-lg shadow focus:outline-none transition-colors;
}

Button.order-table-btn {
    @apply bg-zinc-200 text-zinc-900 border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 transition-colors;
}
</style>

<style>
/* Calendar input hover and focus styles - light mode only */
html:not(.dark) .p-calendar .p-inputtext:not(.p-disabled):hover {
    border-color: #0f1338 !important;
}

html:not(.dark) .p-calendar .p-inputtext.p-focus {
    border-color: #0f1338 !important;
    box-shadow: 0 0 0 0.1rem rgba(15, 19, 56, 0.8) !important;
}

/* Calendar button styles - light mode */
html:not(.dark) .p-calendar .p-button {
    background-color: #0f1338 !important;
    border-color: #0f1338 !important;
    color: white !important;
}

html:not(.dark) .p-calendar .p-button:hover:not(:disabled) {
    background-color: #1a1f4d !important;
    border-color: #1a1f4d !important;
}

/* Calendar button styles - dark mode */
html.dark .p-calendar .p-button {
    background-color: #D9D9D9 !important;
    border-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

html.dark .p-calendar .p-button:hover:not(:disabled) {
    background-color: #c4c4c4 !important;
    border-color: #c4c4c4 !important;
}

.dark .expenses-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .expenses-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .expenses-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .expenses-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .expenses-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child {
    /* background-color: #991b1b !important; */
    color: #ff7070 !important;
    font-weight: 600 !important;
}

.dark .expenses-table.p-datatable .p-datatable-tbody > tr:last-child > td {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .expenses-table.p-datatable .p-datatable-tbody > tr:last-child > td:first-child {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}
</style>